import { Injectable } from '@angular/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { AccessLevel } from '@ftr/distribution-contracts'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { patch, removeItem, updateItem } from '@ngxs/store/operators'
import { groupCartItems } from '../../utils'
import { AddCartItems, ClearCartItems, RemoveCartItems, SetCartCourtSystem, UpdateCartItem } from './cart.actions'
import {
  CART_STATE_DEFAULT,
  CartCourtSystem,
  CartGroup,
  CartItem,
  CartStateModel,
  CartStateModelSchema,
} from './cart.model'

export const CART_STATE_KEY = 'cart'

@State<CartStateModel>({
  name: CART_STATE_KEY,
  defaults: CART_STATE_DEFAULT,
})
@Injectable()
export class CartState {
  @Selector()
  static cartCourtSystem(state: CartStateModel): CartCourtSystem | undefined {
    return state.courtSystem
  }

  @Selector()
  static cartItems(state: CartStateModel): CartItem[] {
    return state.cartItems
  }

  @Selector()
  static sessionIds(state: CartStateModel): Set<Uuid> {
    return new Set(CartState.cartItems(state).map(({ sessionId }) => sessionId))
  }

  @Selector()
  static numCartItems(state: CartStateModel): number {
    return CartState.cartItems(state).length
  }

  @Selector()
  static groupedCartItems(state: CartStateModel): CartGroup[] {
    const items = CartState.cartItems(state)
    return groupCartItems(items)
  }

  @Selector()
  static isAllPublic(state: CartStateModel): boolean {
    const cartItems = CartState.cartItems(state)
    return cartItems.length > 0 && cartItems.every(cartItem => cartItem.rule.accessLevel === AccessLevel.Public)
  }

  @Action(AddCartItems)
  addCartItems(ctx: StateContext<CartStateModel>, { items }: AddCartItems): void {
    const cartItems = [...ctx.getState().cartItems]

    for (const itemModel of items) {
      const existingIndex = cartItems.findIndex(item => item.sessionId === itemModel.sessionId)
      if (existingIndex >= 0) {
        cartItems[existingIndex] = itemModel
      } else {
        cartItems.push(itemModel)
      }
    }

    ctx.setState(patch({ cartItems }))
  }

  @Action(RemoveCartItems)
  removeCartItem(ctx: StateContext<CartStateModel>, { itemIds }: RemoveCartItems): void {
    ctx.setState(
      patch({
        cartItems: removeItem<CartItem>(item => itemIds.includes(item.id)),
      }),
    )
  }

  @Action(UpdateCartItem)
  updateCartItem(ctx: StateContext<CartStateModel>, { itemId, updates }: UpdateCartItem): void {
    const existingItem = ctx.getState().cartItems.find(i => i.id === itemId)
    if (!existingItem) return

    const cartItemModel = { ...existingItem, ...updates }
    ctx.setState(
      patch({
        cartItems: updateItem<CartItem>(existing => existing.id === itemId, cartItemModel),
      }),
    )
  }

  @Action(ClearCartItems)
  clearCartItems(ctx: StateContext<CartStateModel>): void {
    ctx.patchState({ cartItems: [] })
  }

  @Action(SetCartCourtSystem)
  setCourtSystemId(
    ctx: StateContext<CartStateModel>,
    { courtSystemId, courtSystemName, courtTimezoneId }: SetCartCourtSystem,
  ): void {
    ctx.setState(
      patch<CartStateModel>({ courtSystem: { id: courtSystemId, name: courtSystemName, timezoneId: courtTimezoneId } }),
    )
  }
}

export function deserializeCartState(state: unknown): CartStateModel {
  try {
    return CartStateModelSchema.parse(state)
  } catch {
    return CART_STATE_DEFAULT
  }
}
